import jwtDecode from "jwt-decode"
import pako from "pako"
import base64 from "base-64"

interface RestrictionQuery {
  service: string
  resource: string
  action: string
  restriction?: string
}

export default class PerkboxToken {
  permissions: any

  claims: any

  myToken: string

  /**
   * PerkboxToken Constructor
   * @param  {string} myToken JWT Token
   */
  constructor(myToken: string) {
    const decoded = jwtDecode(myToken)
    if (decoded == null) {
      throw new Error("Invalid token")
    }
    this.claims = decoded

    const _buffer = base64.decode(this.claims.aut)
    const restored: any = JSON.parse(pako.inflate(_buffer, {to: "string"}))
    this.permissions = restored
    this.myToken = myToken
  }

  /**
   * Returns the actual access token
   * @return {string} Access token
   */
  getToken(): string {
    return this.myToken
  }

  /**
   * Get a claim from the parsed token (throws error on fail)
   * @param  {string} val Claim name
   * @return {string}     Claim value
   */
  need(val: string): string {
    const res = this.get(val)
    if (!res) throw new Error(`Claim ${val} not found`)
    return res
  }

  /**
   * Get a claim from the parsed token
   * @param  {string}         val     Claim name
   * @return {string|null}            Claim value or null
   */
  get(val: string): string {
    return this.claims[val] || null
  }

  /**
   * Get all claims
   * @return {object}
   */
  getClaims(): any {
    return this.claims
  }

  /**
   * Check if a specific permission exists in the token
   * @param  {string}  service  Service name
   * @param  {string}  resource Resource name
   * @param  {string}  action   Action name (CRUD)
   * @return {Boolean}
   */
  isAllowed({service, resource, action, restriction}: RestrictionQuery): boolean {
    return !!this.getRestrictions({service, resource, action, restriction})
  }

  /**
   * Check if a specific restriction is allowed
   * @param  {string}  service  Service name
   * @param  {string}  resource Resource name
   * @param  {string}  action   Action name (CRUD)
   * @return {Array}
   */
  getRestrictions({service, resource, action, restriction}: RestrictionQuery) {
    const permissions = this.permissions

    const _service = permissions[service]
    if (!_service) return false

    const _resource = _service[resource]
    if (!_resource) return false

    const allowedAction = _resource[`+${action}`]
    if (!allowedAction) return false

    if (!restriction) {
      return _resource[`+${action}`]
    }

    return _resource[`+${action}`][`*${restriction}`]
  }

  /**
   * Get an object of requirements for a specific action
   * @param  {string}  service  Service name
   * @param  {string}  resource Resource name
   * @param  {string}  action   Action name (CRUD)
   * @return {Boolean}
   */
  getRequirements(service: string, resource: string, action: string): boolean {
    const allowedAction = this.permissions[service][resource][`+${action}`]
    if (!allowedAction) return false

    return allowedAction
  }
}
